import { useAuth0 } from "@auth0/auth0-react";
import {
  Table,
  TableBody,
  TableContainer,
  Button,
  TablePagination,
  Paper,
  Box,
  Alert,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";

import API_Calls_ManagementToken from "@/api/calls/API_Calls_ManagementToken";
import API_Calls_UsersTable from "@/api/calls/API_Calls_UsersTable";
import Notifications from "@/components/Notifications/Notifications";
import SearchBar from "@/components/SearchBar/SearchBar";
import AffiliateSelector from "@/components/sharedComponents/AffiliateSelector";
import UsersModal from "@/components/Users/UsersModal/UsersModal";
import UsersTableHead from "@/components/Users/UsersTable/UsersTableHead";
import UsersTableRow from "@/components/Users/UsersTable/UsersTableRow";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import {
  canManageAllUsers,
  canManageOwnUsers,
} from "@/utils/permissionsHandler";

import "@/components/Users/Users.css";

// ATTENTION: This page is being used in both the membership workflow and the political workflow. Keep this is mind as you hack.
const Users = () => {
  const { getManagementToken } = API_Calls_ManagementToken();
  const { user } = useAuth0();
  const { workflow } = useUpdateDataContext();
  const [affiliates, setAffiliates] = useState([]);
  const { getUsers, getAffiliates } = API_Calls_UsersTable();
  const [users, setUsers] = useState([]);
  const [token, setToken] = useState(undefined);
  const [managementToken, setManagementToken] = useState(undefined);
  const [pageData, setPageData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [modalState, setModalState] = useState("");
  const [dataToAlter, setDataToAlter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [notification, setNotification] = useState({});
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("uploadDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [chosenAffiliate, setChosenAffiliate] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    const init = async () => {
      const tokens = await getManagementToken();
      if (tokens.length) {
        setToken(tokens[0]);
        setManagementToken(tokens[1]);
      } else {
        setLoading(false);
      }
    };
    init();
    // getManagementToken is recreated each render and intentionally
    // fires only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const setUpTable = async () => {
      if (canManageAllUsers(user)) {
        const affiliateResponse = await getAffiliates(managementToken);
        if (affiliateResponse) {
          setAffiliates(affiliateResponse);
        }
      } else {
        fetchUsers(user.org_id);
      }
    };
    if (token && managementToken) {
      setUpTable();
    }
    // fetchUsers is defined in this component and recreated every render;
    // intentionally react only to token/managementToken changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, managementToken]);

  const handleAffiliateSelection = async (e, chosenAffiliate) => {
    setChosenAffiliate(chosenAffiliate);
    if (chosenAffiliate) {
      fetchUsers(chosenAffiliate.id);
    } else {
      setUsers([]);
    }
  };

  const fetchUsers = async (affiliateId) => {
    setLoading(true);
    let users = await getUsers(affiliateId, managementToken, workflow);
    if (users?.length > 0) {
      users = users.map((user) => ({
        ...user,
        affiliate_id: affiliateId,
      }));
      setUsers(users);
    } else {
      setUsers([]);
    }
    setLoading(false);
  };

  async function updateTable(alteredObject, type) {
    let temp;
    if (type === "edit") {
      temp = users.map((item) =>
        item.user_id === alteredObject.user_id ? alteredObject : item
      );
    } else if (type === "delete") {
      temp = users.filter((item) => item.user_id !== alteredObject.user_id);
    }
    setUsers(temp);
    setFilteredData(temp);
    setSearchTerm("");
  }

  useEffect(() => {
    const temp = filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    setPageData(temp);
  }, [rowsPerPage, page, filteredData]);

  const sortHandler = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const temp = [...users];
    temp.sort(function (a, b) {
      const textA = a[orderBy];
      const textB = b[orderBy];
      if (order === "asc") {
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      } else {
        return textA > textB ? -1 : textA < textB ? 1 : 0;
      }
    });
    setUsers(temp);
    // Intentionally only re-sort when order/orderBy change; depending
    // on `users` would cause infinite setUsers→effect loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orderBy]);

  useEffect(() => {
    setFilteredData(users);
  }, [users]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
    const tempSearchTerm = searchTerm.toLowerCase();
    const tempArray = users.filter((user) => {
      return (
        (user.name && user.name.toLowerCase().includes(tempSearchTerm)) ||
        (user.role && user.role.toLowerCase().includes(tempSearchTerm)) ||
        (user.email && user.email.toLowerCase().includes(tempSearchTerm)) ||
        (user.local && user.local.toLowerCase().includes(tempSearchTerm))
      );
    });
    setFilteredData(tempArray);
  }, [searchTerm, users]);

  const editRecord = (individual) => {
    setDataToAlter(individual);
    setModalState("edit");
    handleOpen();
  };

  const deleteRecord = (individual) => {
    setDataToAlter(individual);
    setModalState("delete");
    handleOpen();
  };

  const inviteUser = () => {
    setModalState("invite");
    handleOpen();
  };

  if (!canManageOwnUsers(user)) {
    return (
      <Alert severity="error" className="no-mgmt-token-msg">
        Apologies! You do not have access to this page!
      </Alert>
    );
  }
  return (
    <>
      <TableContainer id="users-table-container" component={Paper}>
        <div className="user-table-header-ui">
          <Box sx={{ display: "flex" }}>
            {canManageAllUsers(user) && (
              <Box sx={{ marginRight: "20px" }}>
                <AffiliateSelector
                  affiliates={affiliates}
                  onChange={handleAffiliateSelection}
                  selectedLocal={chosenAffiliate}
                />
              </Box>
            )}

            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </Box>
          <Button className="siteBlue" onClick={inviteUser}>
            Invite User
          </Button>
        </div>
        <Table data-testid="users-table">
          <UsersTableHead
            order={order}
            orderBy={orderBy}
            sortHandler={sortHandler}
            user={user}
          />
          <TableBody>
            {loading ? (
              <tr>
                <td>
                  <Typography sx={{ padding: "15px" }}>Loading ...</Typography>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td>
                  <Typography sx={{ padding: "15px" }}>
                    No users to show
                  </Typography>
                </td>
              </tr>
            ) : (
              <>
                {pageData.map((individual, index) => (
                  <UsersTableRow
                    key={`${individual.name}-${index}`}
                    individual={individual}
                    editRecord={editRecord}
                    deleteRecord={deleteRecord}
                  />
                ))}
              </>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      {open && (
        <UsersModal
          open={open}
          handleClose={handleClose}
          modalState={modalState}
          dataToAlter={dataToAlter}
          updateTable={updateTable}
          managementToken={managementToken}
          affiliates={affiliates}
          setNotification={setNotification}
        />
      )}
      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
};
export default Users;
